const BASE = '/api';

export class ApiError extends Error {
  /**
   * Full parsed response body, when the server returned JSON. Used by the
   * outbox sender to capture the conflict payload (reason, detail,
   * server_version) on 409 responses without re-fetching.
   */
  public body: Record<string, unknown> | null;

  /**
   * 3.9x — server-issued request_id (echoed via X-Request-Id response
   * header). The outbox + Sentry breadcrumbs surface this so a 500 in
   * prod traces end-to-end: browser -> server log line -> PG query.
   */
  public requestId: string | null;

  constructor(
    public status: number,
    message: string,
    body: Record<string, unknown> | null = null,
    requestId: string | null = null,
  ) {
    super(message);
    this.name = 'ApiError';
    this.body = body;
    this.requestId = requestId;
  }
}

// 3.9x — generate an X-Request-Id on the client so the server logs and
// the browser console share the same uuid. Server happily uses whatever
// we send. crypto.randomUUID is available on all our target browsers
// (iOS 14+ / Android Chrome). Fallback: a fast pseudo-uuid.
function makeRequestId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* fall through */
  }
  // crude fallback — not RFC 4122 but unique enough for log correlation
  return `c-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

function getToken(): string | null {
  try {
    const raw = localStorage.getItem('manga-auth');
    if (!raw) return null;
    return JSON.parse(raw)?.state?.token ?? null;
  } catch {
    return null;
  }
}

// Sprint 3.5f — Bug 3: if a request comes back 401/No autenticado,
// the client-side token is stale (Safari nuked storage, session expired,
// or backend bounced). Clear it and tell the app to bounce back to PIN.
// One dispatched event per page-load avoids spamming listeners during a
// burst of failed requests.
let _authBouncedThisLoad = false;
function handleAuthLoss(reason: string) {
  if (_authBouncedThisLoad) return;
  _authBouncedThisLoad = true;
  try {
    localStorage.removeItem('manga-auth');
  } catch {
    /* swallow */
  }
  try {
    window.dispatchEvent(
      new CustomEvent('manga-auth-lost', { detail: { reason } }),
    );
  } catch {
    /* swallow */
  }
}

export async function apiFetch<T>(
  path: string,
  opts: RequestInit = {},
): Promise<T> {
  const token = getToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string>),
  };
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  // 3.9x — stamp every request with an X-Request-Id. Server echoes it back
  // so we always know what to grep for in Railway logs after a failure.
  if (!headers['X-Request-Id']) {
    headers['X-Request-Id'] = makeRequestId();
  }

  const res = await fetch(`${BASE}/${path}`, { ...opts, headers });
  const reqId = res.headers.get('X-Request-Id') ?? headers['X-Request-Id'] ?? null;

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: res.statusText }));
    const message = body.error || res.statusText;
    if (res.status === 401) {
      handleAuthLoss(message);
    }
    throw new ApiError(res.status, message, body, reqId);
  }

  return res.json();
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  post: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: JSON.stringify(body) }),
  put: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: JSON.stringify(body) }),
  patch: <T>(path: string, body: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: JSON.stringify(body) }),
  delete: <T>(path: string) =>
    apiFetch<T>(path, { method: 'DELETE' }),
};
